import { View, Text, Image } from '@react-pdf/renderer';
import { possessive as possessiveOf, formatPercent } from '@shared';
import { onboardingPdfReportStyle } from '@/features/pdf-report/lib/pdf-theme';
import { getPronoun, getPossessive } from '@/features/pdf-report/lib/utils';
import { TrendingUpIcon } from '../icons/pdf-icons';
import type { PdfReportData } from '@/features/pdf-report/types';

export function CareJourneySection({ client, illustrations }: { client: Client; illustrations: PdfReportData['illustrations'] }) {
    if (!illustrations) return null;
    const { clientFirstName, intakeSurvey } = client;
    const { ltcAtAge, ltcLikelihoodEver } = client.appliedInferenceSet;
    const { clientHasStartedLtc } = intakeSurvey;
    const possessive = getPossessive(intakeSurvey);
    const isMale = getPronoun(intakeSurvey) === 'he';
    const ltcDecade = `${Math.floor(ltcAtAge / 10) * 10}s`;
    const preDecade = `${Math.floor((ltcAtAge - 10) / 10) * 10}s`;
    const sub = getPronoun(intakeSurvey);
    const slowdown = sub === 'they' ? 'They are slowing down but still capable' : `${sub === 'he' ? 'He' : 'She'} is slowing down but still capable`;
    const currentImg = isMale ? illustrations.youngBoy : illustrations.youngGirl;
    const midImg = isMale ? illustrations.middleAgeMan : illustrations.middleAgeWoman;
    const isEdited = client.inferenceSet.ltcAtAge !== client.appliedInferenceSet.ltcAtAge;

    return (
        <View style={onboardingPdfReportStyle.optionalSectionBox} wrap={false}>
            <View style={onboardingPdfReportStyle.optionalSectionHeader}>
                <TrendingUpIcon />
                <Text style={onboardingPdfReportStyle.optionalSectionHeaderText}>{possessiveOf(clientFirstName)} Care Journey</Text>
            </View>
            <View style={onboardingPdfReportStyle.optionalSectionBody}>
                {!clientHasStartedLtc && (
                    <Text style={onboardingPdfReportStyle.optionalNarrative}>We predict, in {possessive} {ltcDecade}, {clientFirstName} will need help with daily activities.</Text>
                )}
                <View style={onboardingPdfReportStyle.optionalGrid}>
                    <View style={onboardingPdfReportStyle.illustrationCard}>
                        <View style={onboardingPdfReportStyle.illustrationWrapper}><Image style={onboardingPdfReportStyle.illustrationImage} src={currentImg} /></View>
                        <Text style={onboardingPdfReportStyle.illustrationTitle}>Current State</Text>
                        <Text style={onboardingPdfReportStyle.illustrationSubtitle}>Independent and fully capable</Text>
                    </View>
                    <View style={onboardingPdfReportStyle.illustrationCard}>
                        <View style={onboardingPdfReportStyle.illustrationWrapper}><Image style={onboardingPdfReportStyle.illustrationImage} src={midImg} /></View>
                        <Text style={onboardingPdfReportStyle.illustrationTitle}>By {possessive} {preDecade}</Text>
                        <Text style={onboardingPdfReportStyle.illustrationSubtitle}>{slowdown}</Text>
                    </View>
                    <View style={onboardingPdfReportStyle.illustrationCard}>
                        <View style={onboardingPdfReportStyle.illustrationWrapper}><Image style={onboardingPdfReportStyle.illustrationImage} src={illustrations.agedWoman} /></View>
                        <Text style={onboardingPdfReportStyle.illustrationTitle}>In need of LTC help</Text>
                        <Text style={onboardingPdfReportStyle.illustrationSubtitle}>Unable to perform daily activities alone</Text>
                    </View>
                </View>
                {!clientHasStartedLtc && (
                    <View style={onboardingPdfReportStyle.optionalStatsRow}>
                        <View style={onboardingPdfReportStyle.optionalStatItem}>
                            <Text style={onboardingPdfReportStyle.optionalStatLabel}>Likelihood of Needing LTC</Text>
                            <Text style={onboardingPdfReportStyle.optionalStatValue}>{formatPercent(ltcLikelihoodEver, 0)}</Text>
                        </View>
                        <View style={onboardingPdfReportStyle.optionalStatItem}>
                            <Text style={onboardingPdfReportStyle.optionalStatLabel}>Care Expected to Begin at Age</Text>
                            <Text style={onboardingPdfReportStyle.optionalStatValue}>{ltcAtAge}</Text>
                            {isEdited && <Text style={onboardingPdfReportStyle.optionalStatUnit}>(edited)</Text>}
                        </View>
                    </View>
                )}
            </View>
        </View>
    );
}
